import { getCountryCallingCode, isValidPhoneNumber, type CountryCode } from "libphonenumber-js";

export type PlaceInfo = {
  city: string;
  country: string;
  countryCode: CountryCode;
  formatted: string;
};

/**
 * Reverse-geocode coordinates into a real-world place.
 * Returns null when the point is not inside any country (ocean / nowhere).
 */
export async function reverseGeocode(lat: number, lng: number): Promise<PlaceInfo | null> {
  const url = `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`;
  const res = await fetch(url);
  if (!res.ok) throw new Error("Could not verify your location. Please try again.");
  const d = (await res.json()) as {
    city?: string;
    locality?: string;
    principalSubdivision?: string;
    countryName?: string;
    countryCode?: string;
  };
  const countryCode = (d.countryCode || "").toUpperCase();
  if (!countryCode || !d.countryName) return null;
  const city = (d.city || d.locality || d.principalSubdivision || "").trim();
  return {
    city,
    country: d.countryName,
    countryCode: countryCode as CountryCode,
    formatted: city ? `${city}, ${d.countryName}` : d.countryName,
  };
}

export function dialCode(countryCode: CountryCode): string {
  try {
    return `+${getCountryCallingCode(countryCode)}`;
  } catch {
    return "";
  }
}

/** Phone must be a valid number for the country the user is physically in. */
export function phoneMatchesCountry(phone: string, countryCode: CountryCode | null): boolean {
  const v = phone.trim();
  if (!v) return false;
  try {
    return countryCode ? isValidPhoneNumber(v, countryCode) : isValidPhoneNumber(v);
  } catch {
    return false;
  }
}

export type { CountryCode };
